import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Progress } from "@/components/ui/progress";
import { cn } from "@/lib/utils";

const STATUS_CONFIG = {
  "Planning": { header: "bg-purple-100", text: "text-purple-700", dot: "bg-purple-500" },
  "Active": { header: "bg-green-100", text: "text-green-700", dot: "bg-green-500" },
  "On Hold": { header: "bg-amber-100", text: "text-amber-700", dot: "bg-amber-500" },
  "In Review": { header: "bg-blue-100", text: "text-blue-700", dot: "bg-blue-500" },
  "Completed": { header: "bg-teal-100", text: "text-teal-700", dot: "bg-teal-500" }
};

const COLUMNS = ["Planning", "Active", "On Hold", "In Review", "Completed"];

const KanbanCard = ({ project, onDragStart, onDragEnd }) => {
  const navigate = useNavigate();
  const statusConfig = STATUS_CONFIG[project.status] || STATUS_CONFIG["Planning"];
  
  const isOverdue = project.dueDate && new Date(project.dueDate) < new Date() && project.status !== "Completed";
  
  const formatDate = (date) => {
    if (!date) return "";
    return new Date(date).toLocaleDateString("en-US", { month: "short", day: "numeric" });
  };

  const getInitials = (name) => {
    if (!name) return "U";
    return name.split(" ").map(n => n[0]).join("").toUpperCase().slice(0, 2);
  };

  const getAvatarColor = (index) => {
    const colors = ["from-blue-500", "from-purple-500", "from-pink-500", "from-green-500", "from-amber-500"];
    return colors[index % colors.length];
  };

  const assignedMembers = project.assignedMembers || [];
  const displayedMembers = assignedMembers.slice(0, 2);

  const handleDragStart = (e) => {
    e.dataTransfer.setData("projectId", project.id);
    e.dataTransfer.effectAllowed = "move";
    onDragStart && onDragStart(project.id);
  };

  const getPriorityColor = (priority) => {
    if (priority === "High") return "bg-red-500";
    if (priority === "Medium") return "bg-amber-500";
    return "bg-green-500";
  };

  return (
    <Card 
      className="cursor-grab active:cursor-grabbing shadow-sm hover:shadow-md transition-all hover:-translate-y-0.5"
      draggable
      onDragStart={handleDragStart}
      onDragEnd={onDragEnd}
      onClick={() => navigate(`/projects/${project.id}`)}
    >
      <CardContent className="p-3 space-y-2">
        <div className="flex items-start justify-between gap-2">
          <div className="w-2 h-2 rounded-full" style={{ backgroundColor: project.color || "#6366F1" }} />
          <span className="font-semibold text-navy text-sm line-clamp-2">{project.name}</span>
        </div>

        <div className="flex items-center gap-2">
          <span className={cn("w-2 h-2 rounded-full", getPriorityColor(project.priority))} />
          <span className="text-xs text-gray-500">{project.priority || "Medium"}</span>
        </div>

        <div className="flex items-center gap-2">
          <Progress value={project.progress || 0} className="flex-1 h-1.5" />
          <span className="text-xs text-gray-500">{project.progress || 0}%</span>
        </div>

        {assignedMembers.length > 0 && (
          <div className="flex items-center -space-x-1">
            {displayedMembers.map((memberId, index) => (
              <div
                key={index}
                className={cn(
                  "w-6 h-6 rounded-full bg-gradient-to-br flex items-center justify-center text-white text-xs font-medium border border-white",
                  getAvatarColor(index)
                )}
              >
                {getInitials(`User ${index + 1}`)}
              </div>
            ))}
          </div>
        )}

        {project.dueDate && (
          <div className={cn("text-xs", isOverdue ? "text-red-600 font-medium" : "text-gray-400")}>
            Due {formatDate(project.dueDate)}
          </div>
        )}
      </CardContent>
    </Card>
  );
};

const KanbanColumn = ({ status, projects, onDrop, onDragOver, onDragStart, onDragEnd }) => {
  const statusConfig = STATUS_CONFIG[status] || STATUS_CONFIG["Planning"];
  
  const handleDragOver = (e) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = "move";
  };

  const handleDrop = (e) => {
    e.preventDefault();
    const projectId = e.dataTransfer.getData("projectId");
    onDrop && onDrop(projectId, status);
  };

  return (
    <div 
      className="flex-shrink-0 w-72"
      onDragOver={handleDragOver}
      onDrop={handleDrop}
    >
      <div className={cn("flex items-center justify-between p-3 rounded-t-lg", statusConfig.header)}>
        <div className="flex items-center gap-2">
          <span className={cn("w-2 h-2 rounded-full", statusConfig.dot)} />
          <span className={cn("font-semibold text-sm", statusConfig.text)}>{status}</span>
        </div>
        <Badge variant="secondary" className="text-xs">
          {projects.length}
        </Badge>
      </div>
      
      <div className="bg-slate-50 p-2 rounded-b-lg min-h-[400px] space-y-2">
        {projects.map(project => (
          <KanbanCard 
            key={project.id} 
            project={project}
            onDragStart={onDragStart}
            onDragEnd={onDragEnd}
          />
        ))}
        {projects.length === 0 && (
          <div className="text-center py-8 text-gray-400 text-sm">
            Drop projects here
          </div>
        )}
      </div>
    </div>
  );
};

const KanbanBoard = ({ projects, onStatusChange }) => {
  const [draggedProject, setDraggedProject] = useState(null);

  const handleDragStart = (projectId) => {
    setDraggedProject(projectId);
  };

  const handleDragEnd = () => {
    setDraggedProject(null);
  };

  const handleDrop = (projectId, newStatus) => {
    onStatusChange && onStatusChange(projectId, newStatus);
  };

  const getProjectsByStatus = (status) => {
    return projects.filter(p => p.status === status);
  };

  return (
    <div className="flex gap-4 overflow-x-auto pb-4">
      {COLUMNS.map(status => (
        <KanbanColumn
          key={status}
          status={status}
          projects={getProjectsByStatus(status)}
          onDrop={handleDrop}
          onDragStart={handleDragStart}
          onDragEnd={handleDragEnd}
        />
      ))}
    </div>
  );
};

export default KanbanBoard;